import logoUrl from "@/assets/logo-icon.png";
import { AUTH_DISABLED } from "@/lib/featureFlags";

/**
 * Loads an image from a URL and returns it as an HTMLImageElement.
 */
function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });
}

/**
 * Adds a "Made with managertable.com" branding badge to the bottom-right
 * corner of an image for free users. Premium users get the image unchanged.
 */
export async function addWatermark(
  dataUrl: string,
  isPremium: boolean
): Promise<string> {
  if (AUTH_DISABLED || isPremium) return dataUrl;

  return new Promise((resolve) => {
    const img = new Image();
    img.onload = async () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        resolve(dataUrl);
        return;
      }

      ctx.drawImage(img, 0, 0);

      // Load logo
      let logoImg: HTMLImageElement | null = null;
      try {
        logoImg = await loadImage(logoUrl);
      } catch {
        // Continue without logo if it fails
      }

      const text = "Made with managertable.com";
      const badgeHeight = Math.max(28, img.height * 0.04);
      const fontSize = badgeHeight * 0.5;
      const padding = badgeHeight * 0.4;
      const logoSize = badgeHeight * 0.6;
      const logoGap = badgeHeight * 0.25;
      const margin = Math.max(8, img.width * 0.02);
      const cornerRadius = badgeHeight * 0.35;

      ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
      const textWidth = ctx.measureText(text).width;

      const totalWidth = padding + (logoImg ? logoSize + logoGap : 0) + textWidth + padding;

      const x = (img.width - totalWidth) / 2;
      const y = margin;

      // Draw rounded rect background
      ctx.beginPath();
      ctx.roundRect(x, y, totalWidth, badgeHeight, cornerRadius);
      ctx.fillStyle = "rgba(0, 0, 0, 0.6)";
      ctx.fill();

      // Draw logo
      let textX = x + padding;
      if (logoImg) {
        const logoY = y + (badgeHeight - logoSize) / 2;
        ctx.drawImage(logoImg, textX, logoY, logoSize, logoSize);
        textX += logoSize + logoGap;
      }

      // Draw text
      ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
      ctx.font = `bold ${fontSize}px system-ui, -apple-system, sans-serif`;
      ctx.textBaseline = "middle";
      ctx.fillText(text, textX, y + badgeHeight / 2);

      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}
